import AppDispatcher from "../Dispatcher";
import EventEmitter from "events";
import { AppActionConstants } from "../Actions/AppActionConstants";
import _ from "lodash";

const toArray = (value) => {
	if (value == null) {
		return [];
	}
	return Array.isArray(value) ? value : [value];
};

const getFieldNameFromErrorPath = (errorPath) => {
	const pathSegments = errorPath.split(".");
	const fieldName = pathSegments[pathSegments.length - 1];
	return fieldName.replace(/\[\d+\]/g, "");
};

const addFieldError = (errors, fieldName, messages) => {
	const message = toArray(messages).join(" ");
	if (!fieldName || !message) {
		return;
	}
	errors[fieldName] = errors[fieldName] ? `${errors[fieldName]} ${message}` : message;
};

const addFormErrors = (errors, messages) => {
	const normalizedMessages = toArray(messages).filter(Boolean);
	if (normalizedMessages.length) {
		errors.Errors = [...(errors.Errors || []), ...normalizedMessages];
	}
};

const addInputErrors = (errors, inputErrors) => {
	toArray(inputErrors).forEach((inputError) => {
		Object.keys(inputError || {}).forEach((fieldName) => {
			addFieldError(errors, fieldName, inputError[fieldName]);
		});
	});
};

const normalizePlacementErrors = (responseErrors) => {
	const errors = {};

	if (responseErrors?.errors) {
		Object.keys(responseErrors.errors).forEach((errorPath) => {
			addFieldError(errors, getFieldNameFromErrorPath(errorPath), responseErrors.errors[errorPath]);
		});
		addFormErrors(errors, Object.keys(errors).length ? null : responseErrors.title);
		return errors;
	}

	const errorResults = responseErrors?.Object || responseErrors?.InputErrors || responseErrors?.Errors ? [responseErrors] : Object.values(responseErrors || {});
	errorResults.forEach((result) => {
		const errorObject = result?.Object || result;
		addInputErrors(errors, errorObject?.InputErrors);
		addFormErrors(errors, errorObject?.Errors);
	});

	return Object.keys(errors).length ? errors : responseErrors;
};

class PlacementDataStore extends EventEmitter {
	constructor() {
		super();

		this.placements = {
			config: [],
			data: [],
			errors: [],
			parentData: [],
			filteredData: [],
			filterPackage: null,
			filterPackageID: null,
			deletedPackageID: null,
			syncStatus: null,
			deletePlacementsErrors: []
		};

		AppDispatcher.register((action) => {
			switch (action.type) {
				case AppActionConstants.fetchPlacements:
					const hierarchyData = this.updateExistingValues(action.value.data, action.value.config);
					this.placements.config = action.value.config;
					this.placements.data = hierarchyData;
					this.emit("placements_loaded");
					break;
				case AppActionConstants.refetchPlacementsSuccess:
					const refetchedPlacementData = this.updateExistingValues(action.value, this.placements.config);
					this.placements.data = refetchedPlacementData;
					this.emit("placements_refetched");
				case AppActionConstants.fetchOnlyPlacements:
					const placementConfig = this.placements.config;
					const onlyPlacements = this.updateExistingValues(action.value, placementConfig);
					if (action.placementsSentToPrisma) {
						onlyPlacements.forEach((placement) => {
							if (action.placementsSentToPrisma.includes(placement.PlacementID) && placement.ExternalSystemStatusID === "1") {
								placement.ExternalSystemStatusID = "4";
								placement.ExternalSystemResponse = "Sent to Queue";
							}
						});
					}
					this.placements.data = onlyPlacements;
					break;
				case AppActionConstants.packageUpdated:
					const updatedHierarchyData = this.updateExistingValues(action.value.data, action.value.config);
					this.placements.config = action.value.config;
					this.placements.data = updatedHierarchyData;
					if (this.getFilterPackageID() === action.packageID) {
						let data = JSON.parse(JSON.stringify(updatedHierarchyData));
						data = data.filter((placement) => {
							return placement.PackageID === action.packageID;
						});
						this.placements.filteredData = data;
					}
					this.emit("packages_loaded_refresh");
					break;
				case AppActionConstants.fetchPlacementConfig:
					this.placements.config = action.value;
					this.emit("placement_config_loaded");
					break;
				case AppActionConstants.putPlacementDataSuccess:
					this.placements.data = [action.value, ...this.placements.data];
					this.emit("placement_success");
					break;
				case AppActionConstants.addPlacementDataFail:
					this.placements.errors = normalizePlacementErrors(action.value);
					this.emit("add_placement_error");
					break;
				case AppActionConstants.putPlacementDataFail:
					this.placements.errors = normalizePlacementErrors(action.value);
					this.emit("error_placement");
					break;
				case AppActionConstants.deletePlacementSuccess:
					this.placements.data = action.value;
					if (this.placements.filteredData.length !== 0) {
						const filteredPlacements = JSON.parse(JSON.stringify(this.placements.filteredData));
						this.placements.filteredData = filteredPlacements.filter((filteredPlacement) => {
							return !action.idsToDelete.includes(filteredPlacement.PlacementID);
						});
					}
					this.emit("placement_delete_success");
					break;
				case AppActionConstants.deletePlacementFail:
					this.placements.deletePlacementsErrors = action.value.Errors;
					this.placements.data = action.value.Placements;
					if (this.placements.filteredData.length !== 0) {
						const filteredPlacements = JSON.parse(JSON.stringify(this.placements.filteredData));
						const placementIDs = action.value.Placements.map((placement) => {
							return placement.PlacementID;
						});
						this.placements.filteredData = filteredPlacements.filter((filteredPlacement) => {
							return placementIDs.includes(filteredPlacement.PlacementID);
						});
					}
					this.emit("placement_delete_fail");
					break;
				case AppActionConstants.addPackage:
					const { config } = this.placements;
					const packageData = { OptionID: action.value.value, OptionLabel: action.value.label };
					if (config.ParentData && config.ParentData.length > 0) {
						config.ParentData[0].Options.push(packageData);
						this.placements.config = config;
					}
					break;
				case AppActionConstants.setFilterPackage:
					this.placements.filterPackage = action.value[0];
					this.placements.filterPackageID = action.value[1];
					this.placements.filteredData = action.clonedPlacements;
					if (action.emitAfter) {
						this.emit("filtered_package");
					}
					break;
				case AppActionConstants.placementCreated:
					this.emit("placement_created");
					break;
				case AppActionConstants.placementFailed:
					this.emit("placement_failed");
					break;
				case AppActionConstants.placementSyncSuccess:
					this.placements.syncStatus = action.value;
					this.emit("sync_success");
					break;
				case AppActionConstants.placementSyncFail:
					this.placements.syncStatus = action.value;
					this.emit("sync_fail");
					break;
				case AppActionConstants.updatePlacementPrismaStatus:
					this.placements.data.forEach((placement) => {
						if (action.value.includes(placement.PlacementID)) {
							placement.ExternalSystemStatusID = "4";
							placement.ExternalSystemResponse = "Sent to Queue";
						}
					});
					this.emit("sync_status_updated");
					break;
				case AppActionConstants.triggerPlacementModal:
					this.emit("openPlacementModal");
					break;
				case AppActionConstants.lockUnlockWorkbookBudgetSuccess:
					this.placements.data = this.placements.data.map((datum) => ({
						...datum,
						IsPrismaBudgetLocked: action.value.locked ? "True" : "False"
					}));
					this.emit("placements_lock_unlock_success");
					break;
				case AppActionConstants.sendPlacementsToGCM:
					this.emit("placements_pushed_to_GCM");
					break;
				default:
					break;
			}
		});
	}

	/*
	Getter function to return the current state
	*/
	getData = () => this.placements.data;
	getFilteredData = () => this.placements.filteredData;
	getConfig = () => this.placements.config;
	getErrors = () => this.placements.errors;
	getFilterPackage = () => this.placements.filterPackage;
	getSyncStatus = () => this.placements.syncStatus;
	getFilterPackageID = () => this.placements.filterPackageID;
	getDeletedPackageID = () => this.placements.deletedPackageID;
	getDeletePlacementErrors = () => this.placements.deletePlacementsErrors;
	resetPlacementErrors = () => {
		this.placements.errors = [];
		this.emit("placement__errors__reset");
	};

	resetPlacementData = () => {
		this.placements.config = [];
	};

	resetSelectedPackage = () => {
		this.placements.filterPackage = null;
	};

	resetSelectedPackageID = () => {
		this.placements.filterPackageID = null;
	};

	resetFilteredPlacementData = () => {
		this.placements.filteredData = [];
	};

	resetDeletedPackageID = () => {
		this.placements.deletedPackageID = null;
	};

	returnFilteredDataRow = (row) => {
		return this.placements.filteredData[row];
	};

	returnDataRow = (row) => {
		return this.placements.data[row];
	};

	updatePlacementsDataManually = (placementsData, bundle) => {
		for (const key in bundle) {
			if (bundle[key].Result === true) {
				const newRow = this.getFilteredData().length !== 0 ? this.returnFilteredDataRow(key) : this.returnDataRow(key); // i.e 6 || Not the JKND35 Code
				if (newRow) {
					newRow.PlacementName = bundle[`${key}`].Object.PlacementName;
					newRow.UniqueID = bundle[`${key}`].Object.UniqueID;
					newRow.ParentUniqueIdCode = bundle[`${key}`].Object.ParentUniqueIdCode;
					newRow.ParentDescription = bundle[`${key}`].Object.ParentDescription;
					newRow.PlacementID = bundle[`${key}`].Object.PlacementID;
					newRow.ExternalSystemStatusID = bundle[`${key}`].Object.ExternalSystemStatusID;
					newRow.ExternalSystemResponse = bundle[`${key}`].Object.ExternalSystemResponse;
				} else {
					const existingRow = placementsData.filter((lineItem) => lineItem.UniqueID === key);
					if (existingRow[0]) {
						existingRow[0].UniqueID = bundle[`${key}`].Object.UniqueID;
						existingRow[0].PlacementName = bundle[`${key}`].Object.PlacementName;
					}

					if (this.getFilterPackage() !== null) {
						const packageFilteredRow = this.getFilteredData().filter((lineItem) => lineItem.UniqueID === key);
						if (packageFilteredRow[0]) {
							packageFilteredRow[0].UniqueID = bundle[`${key}`].Object.UniqueID;
							packageFilteredRow[0].PlacementName = bundle[`${key}`].Object.PlacementName;
						}
					} else if (this.getFilterPackage() === null && this.getFilteredData().length !== 0) {
						const packageFilteredRow = this.getFilteredData().filter((lineItem) => lineItem.UniqueID === key);
						if (packageFilteredRow[0]) {
							packageFilteredRow[0].UniqueID = bundle[`${key}`].Object.UniqueID;
							packageFilteredRow[0].PlacementName = bundle[`${key}`].Object.PlacementName;
						}
					}
				}
			}
		}
	};

	deletePlacementsInPackage = (packageID) => {
		this.placements.data = this.placements.data.filter((placement) => {
			return placement.PackageID !== packageID;
		});
		this.placements.deletedPackageID = packageID;
	};

	updateExistingValues = (data, config) => {
		const newData = data;
		const fieldNames = {};
		const fieldIds = {};
		const excludedFieldNames = [
			"CampaignID",
			"PlacementName_Friendly",
			"ParentDescription",
			"UniqueID",
			"TaxonomyData",
			"MetaData",
			"PlacementName",
			"PlacementID",
			"PackageType",
			"ExternalSystemResponse",
			"ExternalSystemResponse_Orig",
			"ExternalSystemStatusID",
			"IsArchived",
			"IsDeleted",
			"UpdatedBy"
		];
		config.TaxonomyData.forEach((taxonomyField) => {
			fieldNames[taxonomyField.Name] = taxonomyField.ID;
			fieldIds[taxonomyField.ID] = taxonomyField.Name;
		});
		config.MetaData.forEach((metadataField) => {
			fieldNames[metadataField.Name] = metadataField.ID;
			fieldIds[metadataField.ID] = metadataField.Name;
		});
		config.PlacementData.forEach((placementField) => {
			fieldNames[placementField.Name] = placementField.ID;
			fieldIds[placementField.ID] = placementField.Name;
		});
		newData.forEach((dataField) => {
			if (!_.isEmpty(dataField)) {
				Object.keys(dataField).forEach((dataKeyName) => {
					if (!excludedFieldNames.includes(dataKeyName) && !Object.keys(fieldNames).includes(dataKeyName)) {
						let matchingId = null;
						if (dataField.TaxonomyData !== "") {
							JSON.parse(dataField.TaxonomyData).forEach((dataTaxonomyField) => {
								if (matchingId !== null) {
									return;
								}
								if (dataTaxonomyField.DataKeyShortDescr.split(" ").join("") === dataKeyName) {
									matchingId = dataTaxonomyField.DataKeyID;
								}
							});
						}
						if (matchingId === null) {
							if (dataField.MetaData !== "") {
								JSON.parse(dataField.MetaData).forEach((dataMetaDataField) => {
									if (matchingId !== null) {
										return;
									}
									if (dataMetaDataField.DataKeyShortDescr.split(" ").join("") === dataKeyName) {
										matchingId = dataMetaDataField.DataKeyID;
									}
								});
							}
						}
						if (matchingId !== null) {
							if (fieldIds[matchingId]) {
								dataField[fieldIds[matchingId]] = dataField[dataKeyName];
							}
						}
					}
					if (dataKeyName === "CreativeAgencyID" && !dataField.CreativeAgencyOwner) {
						if (config.MetaData) {
							config.MetaData.forEach((dataKey) => {
								if (dataKey.Name === "CreativeAgencyOwner") {
									dataKey.Options.forEach((agencyOwner) => {
										if (agencyOwner.OptionID === dataField[dataKeyName]) {
											dataField.CreativeAgencyOwner = agencyOwner.OptionLabel;
										}
									});
								}
							});
						}
					}
				});
			}
		});
		return newData;
	};
}

export default new PlacementDataStore();
